import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Search, Plus, Minus, Send, CheckCircle2,
  UtensilsCrossed, X, ChefHat, ConciergeBell, StickyNote,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";
import { formatIndianCurrency } from "../utils/Const";

const STATUS_STYLE = {
  FREE:         { bg: "bg-emerald-50",  border: "border-emerald-300", text: "text-emerald-700", label: "Free" },
  OPEN:         { bg: "bg-amber-50",    border: "border-amber-300",   text: "text-amber-700",   label: "Seated" },
  ORDER_PLACED: { bg: "bg-orange-50",   border: "border-orange-300",  text: "text-orange-700",  label: "Ordered" },
  PREPARING:    { bg: "bg-red-50",      border: "border-red-300",     text: "text-red-700",     label: "In Kitchen" },
  READY:        { bg: "bg-blue-50",     border: "border-blue-300",    text: "text-blue-700",    label: "Ready" },
  SERVED:       { bg: "bg-violet-50",   border: "border-violet-300",  text: "text-violet-700",  label: "Served" },
};

// key must match server: floor-section-roomType-unit
const tableKey = (floorName, sectionName, roomType, unit) =>
  `${floorName}-${sectionName}-${roomType || "NA"}-${unit}`;

const itemKey = (it) => `${it.itemId}_${it.variant?.name || ""}`;

export default function CaptainView() {
  const navigate = useNavigate();
  const { restaurantId, staffName } = useAuth();

  // ── Tables screen state ──
  const [layout, setLayout] = useState(null);
  const [activeTables, setActiveTables] = useState({});
  const [activeFloor, setActiveFloor] = useState(null);
  const [loadingTables, setLoadingTables] = useState(true);

  // ── Order screen state ──
  const [order, setOrder] = useState(null);          // server order doc
  const [cart, setCart] = useState([]);              // working copy of items
  const [tableLabel, setTableLabel] = useState("");
  const [openingTable, setOpeningTable] = useState(null);
  const [sending, setSending] = useState(false);

  // ── Menu state ──
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [variantPicker, setVariantPicker] = useState(null); // product awaiting variant choice
  const [notePicker, setNotePicker] = useState(null);       // cart item index for note edit

  const pollRef = useRef(null);

  // ── Fetch tables + status ──
  const fetchTables = useCallback(async () => {
    try {
      const [layoutRes, activeRes] = await Promise.all([
        api.get("/layout").catch(() => null),
        api.get("/orders/active"),
      ]);
      if (layoutRes?.data) {
        setLayout(layoutRes.data);
        setActiveFloor(f => f || layoutRes.data.floors?.[0]?.floorName || null);
      }
      setActiveTables(activeRes.data.tables || {});
    } catch {
      toast.error("Failed to load tables");
    } finally {
      setLoadingTables(false);
    }
  }, []);

  // ── Fetch menu once ──
  const fetchMenu = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const [catRes, prodRes] = await Promise.all([
        api.get(`/newcategories/${restaurantId}`),
        api.get(`/products/${restaurantId}`),
      ]);
      setCategories(catRes.data?.data || catRes.data || []);
      setProducts(prodRes.data?.data || []);
    } catch {
      toast.error("Failed to load menu");
    }
  }, [restaurantId]);

  useEffect(() => { fetchTables(); fetchMenu(); }, [fetchTables, fetchMenu]);

  // Poll table status every 15s while on tables screen
  useEffect(() => {
    if (order) return;
    pollRef.current = setInterval(fetchTables, 15000);
    return () => clearInterval(pollRef.current);
  }, [order, fetchTables]);

  // ── Open a table ──
  const openTable = async (floorName, section, unit) => {
    const key = tableKey(floorName, section.name, section.roomType, unit);
    setOpeningTable(key);
    try {
      const existing = activeTables[key];
      let ord;
      if (existing?.orderId) {
        const res = await api.get(`/orders/${existing.orderId}`);
        ord = res.data.order || res.data;
      } else {
        const res = await api.post("/orders/select-table", {
          floorName,
          sectionName: section.name,
          sectionType: section.type,
          unitNumber: unit,
          roomType: section.roomType || "NA",
        });
        ord = res.data;
      }
      setOrder(ord);
      setCart((ord.items || []).map(i => ({ ...i })));
      setTableLabel(`${section.name} ${unit}${floorName !== section.name ? ` · ${floorName}` : ""}`);
      setSearch("");
      setActiveCategory("all");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to open table");
    } finally {
      setOpeningTable(null);
    }
  };

  const backToTables = () => {
    setOrder(null);
    setCart([]);
    fetchTables();
  };

  // ── Cart ops ──
  const addProduct = (product, variant = null) => {
    const price = variant ? variant.price : product.price;
    const entry = {
      itemId: product._id,
      name: variant ? `${product.productName} (${variant.name})` : product.productName,
      qty: 1,
      price,
      itemTotal: price,
      ...(variant ? { variant: { name: variant.name, price: variant.price } } : {}),
      kotPrinted: false,
      note: "",
    };
    setCart(prev => {
      const idx = prev.findIndex(i => itemKey(i) === itemKey(entry) && !i.kotPrinted);
      if (idx >= 0) {
        return prev.map((i, n) => n === idx ? { ...i, qty: i.qty + 1, itemTotal: (i.qty + 1) * i.price } : i);
      }
      return [...prev, entry];
    });
    setVariantPicker(null);
  };

  const changeQty = (index, delta) => {
    setCart(prev => prev
      .map((i, n) => n === index ? { ...i, qty: i.qty + delta, itemTotal: (i.qty + delta) * i.price } : i)
      .filter(i => i.qty > 0));
  };

  const setItemNote = (index, note) => {
    setCart(prev => prev.map((i, n) => n === index ? { ...i, note } : i));
    setNotePicker(null);
  };

  const pendingItems = cart.filter(i => !i.kotPrinted);
  const sentItems = cart.filter(i => i.kotPrinted);
  const cartTotal = cart.reduce((s, i) => s + i.qty * i.price, 0);
  const pendingCount = pendingItems.reduce((s, i) => s + i.qty, 0);

  // ── Send KOT: save items, then mark printed ──
  const sendKot = async () => {
    if (!order || pendingItems.length === 0) return;
    setSending(true);
    try {
      const payload = cart.map(i => ({
        itemId: i.itemId,
        name: i.name,
        qty: i.qty,
        price: i.price,
        ...(i.variant ? { variant: i.variant } : {}),
        ...(i.addOns?.length ? { addOns: i.addOns } : {}),
        ...(i.note ? { note: i.note } : {}),
      }));
      await api.patch(`/orders/${order._id}/items`, { items: payload });
      const kotRes = await api.post(`/orders/${order._id}/kot-print`, {});
      const updated = kotRes.data.order;
      setOrder(updated);
      setCart((updated.items || []).map(i => ({ ...i })));
      toast.success(`KOT #${kotRes.data.kotRound} sent to kitchen`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send KOT");
    } finally {
      setSending(false);
    }
  };

  // ── Mark served ──
  const markServed = async () => {
    if (!order) return;
    try {
      await api.patch(`/orders/${order._id}/status`, { status: "SERVED" });
      toast.success("Marked as served");
      backToTables();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // ── Menu filtering ──
  const filteredProducts = useMemo(() => {
    let list = products.filter(p => p.isAvailable !== false);
    if (activeCategory !== "all") {
      list = list.filter(p => (p.category?._id || p.category) === activeCategory);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p => p.productName?.toLowerCase().includes(q));
    }
    return list;
  }, [products, activeCategory, search]);

  // ════════════════════════ ORDER SCREEN ════════════════════════
  if (order) {
    return (
      <div className="fixed inset-0 z-40 bg-slate-50 flex flex-col pt-14 lg:pt-16">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-slate-200 shrink-0">
          <button onClick={backToTables} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-black text-slate-800 truncate">{tableLabel}</h1>
            <p className="text-[10px] text-slate-400">Order #{order.orderNumber} · KOT round {order.kotRound || 0}</p>
          </div>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${(STATUS_STYLE[order.status] || STATUS_STYLE.OPEN).bg} ${(STATUS_STYLE[order.status] || STATUS_STYLE.OPEN).text}`}>
            {(STATUS_STYLE[order.status] || STATUS_STYLE.OPEN).label}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Current order items */}
          {cart.length > 0 && (
            <div className="bg-white border-b border-slate-200 px-3 py-3 space-y-1.5">
              {sentItems.length > 0 && (
                <>
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">In kitchen</p>
                  {cart.map((item, idx) => item.kotPrinted && (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                      <span className="flex-1 text-xs text-slate-600 truncate">{item.name}{item.note ? <em className="text-slate-400"> — {item.note}</em> : null}</span>
                      <span className="text-xs text-slate-500">×{item.qty}</span>
                      <span className="text-xs font-semibold text-slate-700 w-14 text-right">₹{formatIndianCurrency(item.qty * item.price)}</span>
                    </div>
                  ))}
                </>
              )}
              {pendingItems.length > 0 && (
                <>
                  <p className="text-[10px] font-black uppercase tracking-wider text-orange-500 pt-1">New — not sent yet</p>
                  {cart.map((item, idx) => !item.kotPrinted && (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <button onClick={() => setNotePicker(idx)} className={`shrink-0 ${item.note ? "text-teal-600" : "text-slate-300"}`} title="Add note">
                        <StickyNote size={13} />
                      </button>
                      <span className="flex-1 text-xs font-medium text-slate-800 truncate">{item.name}{item.note ? <em className="text-slate-400 font-normal"> — {item.note}</em> : null}</span>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => changeQty(idx, -1)} className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 active:bg-slate-200">
                          <Minus size={11} />
                        </button>
                        <span className="text-xs font-bold w-5 text-center">{item.qty}</span>
                        <button onClick={() => changeQty(idx, 1)} className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-600 text-white active:bg-teal-700">
                          <Plus size={11} />
                        </button>
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-14 text-right">₹{formatIndianCurrency(item.qty * item.price)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Menu */}
          <div className="px-3 py-3">
            {/* Search */}
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search menu…"
                className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Category chips */}
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-3 px-3 scrollbar-hide">
              <button
                onClick={() => setActiveCategory("all")}
                className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${activeCategory === "all" ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat._id}
                  onClick={() => setActiveCategory(cat._id)}
                  className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${activeCategory === cat._id ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}
                >
                  {cat.newTitle}
                </button>
              ))}
            </div>

            {/* Product list */}
            <div className="space-y-1.5 mt-1 pb-28">
              {filteredProducts.length === 0 ? (
                <p className="text-center text-xs text-slate-400 py-8">No items found</p>
              ) : filteredProducts.map(product => {
                const hasVariants = product.variants?.length > 0;
                const inCartQty = cart.filter(i => i.itemId === product._id && !i.kotPrinted).reduce((s, i) => s + i.qty, 0);
                return (
                  <button
                    key={product._id}
                    onClick={() => hasVariants ? setVariantPicker(product) : addProduct(product)}
                    className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 active:bg-teal-50 active:border-teal-300 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">{product.productName}</p>
                      <p className="text-[11px] text-slate-500">
                        {hasVariants
                          ? `${product.variants.length} options · from ₹${Math.min(...product.variants.map(v => v.price))}`
                          : `₹${formatIndianCurrency(product.price || 0)}`}
                      </p>
                    </div>
                    {inCartQty > 0 && (
                      <span className="shrink-0 min-w-5 h-5 px-1 flex items-center justify-center rounded-full bg-teal-600 text-white text-[10px] font-black">{inCartQty}</span>
                    )}
                    <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 border border-teal-200">
                      <Plus size={13} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="shrink-0 bg-white border-t border-slate-200 px-3 py-3 flex items-center gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="flex-1">
            <p className="text-[10px] text-slate-400 font-semibold uppercase">Total</p>
            <p className="text-base font-black text-slate-800">₹{formatIndianCurrency(cartTotal)}</p>
          </div>
          {sentItems.length > 0 && pendingItems.length === 0 && order.status !== "SERVED" && (
            <button
              onClick={markServed}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold bg-violet-600 text-white rounded-xl active:bg-violet-700"
            >
              <ConciergeBell size={14} /> Mark Served
            </button>
          )}
          <button
            onClick={sendKot}
            disabled={pendingItems.length === 0 || sending}
            className="flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold bg-teal-600 text-white rounded-xl disabled:opacity-40 active:bg-teal-700"
          >
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            Send KOT{pendingCount > 0 ? ` (${pendingCount})` : ""}
          </button>
        </div>

        {/* Variant picker sheet */}
        {variantPicker && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setVariantPicker(null)}>
            <div className="w-full bg-white rounded-t-2xl p-4 space-y-2" onClick={e => e.stopPropagation()} style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-black text-slate-800">{variantPicker.productName}</h3>
                <button onClick={() => setVariantPicker(null)} className="text-slate-400"><X size={16} /></button>
              </div>
              {variantPicker.variants.filter(v => v.isAvailable !== false).map((v, i) => (
                <button
                  key={i}
                  onClick={() => addProduct(variantPicker, v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl active:bg-teal-50 active:border-teal-300"
                >
                  <span className="text-xs font-bold text-slate-700">{v.name}</span>
                  <span className="text-xs font-black text-teal-600">₹{formatIndianCurrency(v.price)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Note editor sheet */}
        {notePicker !== null && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setNotePicker(null)}>
            <div className="w-full bg-white rounded-t-2xl p-4" onClick={e => e.stopPropagation()} style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
              <h3 className="text-sm font-black text-slate-800 mb-2">Kitchen note — {cart[notePicker]?.name}</h3>
              <input
                autoFocus
                defaultValue={cart[notePicker]?.note || ""}
                placeholder="e.g. less spicy, no onion"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                onKeyDown={e => { if (e.key === "Enter") setItemNote(notePicker, e.target.value); }}
                id="captain-note-input"
              />
              <div className="flex gap-2 mt-3">
                <button onClick={() => setNotePicker(null)} className="flex-1 py-2.5 text-xs font-bold bg-slate-100 text-slate-600 rounded-xl">Cancel</button>
                <button
                  onClick={() => setItemNote(notePicker, document.getElementById("captain-note-input").value)}
                  className="flex-1 py-2.5 text-xs font-bold bg-teal-600 text-white rounded-xl"
                >
                  Save note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════ TABLES SCREEN ════════════════════════
  const floors = layout?.floors || [];
  const currentFloor = floors.find(f => f.floorName === activeFloor) || floors[0];

  return (
    <div className="fixed inset-0 z-40 bg-slate-50 flex flex-col pt-14 lg:pt-16">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-slate-200 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-teal-600/10 border border-teal-600/20 flex items-center justify-center">
          <UtensilsCrossed size={16} className="text-teal-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-sm font-black text-slate-800">Captain</h1>
          <p className="text-[10px] text-slate-400">{staffName ? `Hi ${staffName} · ` : ""}tap a table to take order</p>
        </div>
        <button onClick={() => navigate("/home")} className="text-[10px] font-bold text-slate-500 px-2.5 py-1.5 rounded-lg bg-slate-100 active:bg-slate-200">
          Full POS
        </button>
        <button onClick={fetchTables} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
          <RefreshCw size={15} className={loadingTables ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-3 px-4 py-2 bg-white/60 border-b border-slate-100 overflow-x-auto scrollbar-hide shrink-0">
        {Object.entries(STATUS_STYLE).map(([k, s]) => (
          <span key={k} className="flex items-center gap-1 shrink-0">
            <span className={`w-2.5 h-2.5 rounded-full border ${s.bg} ${s.border}`}></span>
            <span className="text-[10px] text-slate-500">{s.label}</span>
          </span>
        ))}
      </div>

      {/* Floor tabs */}
      {floors.length > 1 && (
        <div className="flex gap-1.5 px-4 py-2 overflow-x-auto scrollbar-hide shrink-0">
          {floors.map(f => (
            <button
              key={f.floorName}
              onClick={() => setActiveFloor(f.floorName)}
              className={`shrink-0 px-3.5 py-1.5 text-xs font-bold rounded-full border transition-colors ${(currentFloor?.floorName === f.floorName) ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600"}`}
            >
              {f.floorName}
            </button>
          ))}
        </div>
      )}

      {/* Table grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {loadingTables ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={20} className="animate-spin text-slate-400" />
          </div>
        ) : !currentFloor ? (
          <div className="text-center py-16">
            <ChefHat size={36} className="mx-auto text-slate-200 mb-2" />
            <p className="text-xs text-slate-400">No table layout configured.</p>
            <p className="text-[11px] text-slate-400 mt-1">Ask the manager to set up floors & tables in Admin.</p>
          </div>
        ) : (
          (currentFloor.sections || []).map(section => (
            <div key={section.name} className="mt-4">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
                {section.name}{section.roomType && section.roomType !== "NA" ? ` (${section.roomType})` : ""}
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {Array.from({ length: section.totalUnits }, (_, i) => i + 1).map(unit => {
                  const key = tableKey(currentFloor.floorName, section.name, section.roomType, unit);
                  const active = activeTables[key];
                  const style = active ? (STATUS_STYLE[active.status] || STATUS_STYLE.OPEN) : STATUS_STYLE.FREE;
                  const isOpening = openingTable === key;
                  return (
                    <button
                      key={unit}
                      onClick={() => openTable(currentFloor.floorName, section, unit)}
                      disabled={isOpening}
                      className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 transition-transform active:scale-95 ${style.bg} ${style.border}`}
                    >
                      {isOpening ? (
                        <RefreshCw size={14} className="animate-spin text-slate-400" />
                      ) : (
                        <>
                          <span className={`text-base font-black ${style.text}`}>{unit}</span>
                          {active && (
                            <span className={`text-[9px] font-bold ${style.text} leading-none`}>
                              ₹{formatIndianCurrency(active.totalAmount || 0)}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
